// src/components/modals/CustomerSelectionModal.jsx
import { useState, useEffect } from 'react'
import { X, Search, User, Phone, Calendar, Building, UserPlus, ChevronDown, Star } from 'lucide-react'
import CustomerService from '../../services/customerService'
import BranchService from '../../services/branchService'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { CustomerModel } from '../../utils/customerModel'

// On-screen keyboard component for mobile number entry
const NumericKeyboard = ({ onKeyPress, onDelete, onClear }) => {
  const keys = [
    ['1', '2', '3'],
    ['4', '5', '6'],  
    ['7', '8', '9'],
    ['Clear', '0', 'Delete']
  ]

  return (
    <div className="grid grid-cols-3 gap-4 p-6 bg-orange-50 rounded-lg">
      {keys.flat().map((key, index) => (
        <button
          key={index}
          onClick={() => {
            if (key === 'Delete') onDelete()
            else if (key === 'Clear') onClear()
            else onKeyPress(key)
          }}
          className={`
            h-16 rounded-lg font-semibold text-lg transition-colors
            ${key === 'Delete' || key === 'Clear' 
              ? 'bg-red-100 text-red-600 hover:bg-red-200' 
              : 'bg-white text-orange-800 hover:bg-orange-100 border border-orange-200 shadow-sm'
            }
          `}
        >
          {key}
        </button>
      ))}
    </div>
  )
}

// Create customer form component
const CreateCustomerForm = ({ onCustomerCreated, onCancel, branchId }) => {
  const [formData, setFormData] = useState({
    name: '',
    mobileNumber: '',
    branch: branchId || '',
    dateOfBirth: '',
    customerType: 'normal', // default to normal customer
    isActive: true
  })
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [branches, setBranches] = useState([])

  useEffect(() => {
    fetchBranches()
    
    // Listen for number pad events
    const handleKeyPress = (event) => {
      if (formData.mobileNumber.length < 10) {
        setFormData(prev => ({
          ...prev,
          mobileNumber: prev.mobileNumber + event.detail
        }));
      }
    };

    const handleDelete = () => {
      setFormData(prev => ({
        ...prev,
        mobileNumber: prev.mobileNumber.slice(0, -1)
      }));
    };

    const handleClear = () => {
      setFormData(prev => ({
        ...prev,
        mobileNumber: ''
      }));
    };

    window.addEventListener('numberPadKeyPress', handleKeyPress);
    window.addEventListener('numberPadDelete', handleDelete);
    window.addEventListener('numberPadClear', handleClear);

    return () => {
      window.removeEventListener('numberPadKeyPress', handleKeyPress);
      window.removeEventListener('numberPadDelete', handleDelete);
      window.removeEventListener('numberPadClear', handleClear);
    };
  }, [formData.mobileNumber])

  const fetchBranches = async () => {
    try {
      const response = await BranchService.getAllBranches()
      if (response?.success) {
        setBranches(response.data?.branches || [])
      }
    } catch (error) {
      console.error('Error fetching branches:', error)
    }
  }

  const validateForm = () => {
    const newErrors = {}
    if (!formData.name.trim()) newErrors.name = 'Name is required'
    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = 'Mobile number is required'
    } else if (!/^[0-9]{10}$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber = 'Mobile number must be exactly 10 digits'
    }
    if (!formData.branch) newErrors.branch = 'Branch is required'
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) return

    setLoading(true)
    try {
      // Format customer data for API (converts mobile number)
      const apiData = CustomerService.formatCustomerForAPI(formData)
      console.log('📤 Creating customer with data:', apiData)
      const response = await CustomerService.createCustomer(apiData)
      console.log('✅ Customer creation API response:', response)
      if (response?.success) {
        console.log('📦 Passing customer data to parent:', response.data)
        onCustomerCreated(response.data)
      }
    } catch (error) {
      if (error.status === 409) {
        setErrors({ mobileNumber: 'Customer with this mobile number already exists' })
      } else {
        setErrors({ general: error.message || 'Failed to create customer' })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    if (name === 'mobileNumber') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10)
      setFormData(prev => ({ ...prev, [name]: digitsOnly }))
    } else {
      setFormData(prev => ({ ...prev, [name]: value }))
    }
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-orange-700 mb-1">
          Customer Name *
        </label>
        <Input
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          placeholder="Enter customer name"
          error={errors.name}
          className="h-12 text-lg border-orange-300 focus:ring-orange-500 focus:border-orange-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-orange-700 mb-1">
          Mobile Number *
        </label>
        <Input
          name="mobileNumber"
          value={formData.mobileNumber}
          onChange={handleInputChange}
          placeholder="Enter 10-digit mobile number"
          error={errors.mobileNumber}
          className="h-12 text-lg border-orange-300 focus:ring-orange-500 focus:border-orange-500"
          maxLength={10}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-orange-700 mb-1">
          Branch *
        </label>
        <div className="relative">
          <select
            name="branch"
            value={formData.branch}
            onChange={handleInputChange}
            className="w-full h-12 text-lg px-3 pr-10 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none bg-white"
          >
            <option value="">Select branch</option>
            {branches.map(branch => (
              <option key={branch._id} value={branch._id}>
                {branch.name}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-orange-400 pointer-events-none" />
        </div>
        {errors.branch && <p className="text-red-500 text-sm mt-1">{errors.branch}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-orange-700 mb-1">
          Date of Birth
        </label>
        <Input
          type="date"
          name="dateOfBirth"
          value={formData.dateOfBirth}
          onChange={handleInputChange}
          className="h-12 text-lg border-orange-300 focus:ring-orange-500 focus:border-orange-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-orange-700 mb-2">
          Customer Type *
        </label>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => {
              setFormData(prev => ({ ...prev, customerType: 'normal' }))
              if (errors.customerType) {
                setErrors(prev => ({ ...prev, customerType: '' }))
              }
            }}
            className={`h-14 rounded-lg font-semibold text-lg transition-all flex items-center justify-center space-x-2 ${
              formData.customerType === 'normal'
                ? 'bg-orange-500 text-white border-2 border-orange-500 shadow-md'
                : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-orange-300'
            }`}
          >
            <User className="h-5 w-5" />
            <span>Normal</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setFormData(prev => ({ ...prev, customerType: 'vip' }))
              if (errors.customerType) {
                setErrors(prev => ({ ...prev, customerType: '' }))
              }
            }}
            className={`h-14 rounded-lg font-semibold text-lg transition-all flex items-center justify-center space-x-2 ${
              formData.customerType === 'vip'
                ? 'bg-gradient-to-br from-yellow-50 to-amber-50 text-amber-700 border-2 border-amber-300 shadow-lg'
                : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-amber-300'
            }`}
          >
            <Star className="h-5 w-5 fill-current" />
            <span>VIP</span>
          </button>
        </div>
        {errors.customerType && <p className="text-red-500 text-sm mt-1">{errors.customerType}</p>}
      </div>

      {errors.general && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-red-600 text-sm">{errors.general}</p>
        </div>
      )}

      <div className="flex space-x-3 pt-4">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          className="flex-1 h-12 text-lg border-orange-300 text-orange-600 hover:bg-orange-50"
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          className="flex-1 h-12 text-lg bg-orange-500 hover:bg-orange-600 text-white"
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Create Customer'}
        </Button>
      </div>
    </form>
  )
}

const CustomerSelectionModal = ({ 
  isOpen, 
  onClose, 
  onCustomerSelect,
  currentCustomerId = null,
  branchId = null 
}) => {
  const [customers, setCustomers] = useState([])
  const [allCustomers, setAllCustomers] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState('customers')
  const [walkingCustomer, setWalkingCustomer] = useState(null)

  useEffect(() => {
    if (isOpen) {
      fetchCustomers()
      setSearchQuery('')
      setActiveTab('customers')
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden'
    } else {
      // Restore body scroll when modal is closed
      document.body.style.overflow = 'auto'
    }
    
    // Cleanup function to restore scroll on unmount
    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [isOpen])

  const fetchCustomers = async () => {
    setLoading(true)
    try {
      const response = await CustomerService.getAllCustomers({ 
        limit: 500,
        isActive: true 
      })
      
      if (response?.success) {
        const customersData = response.data?.customers || []
        
        const walking = customersData.find(customer => 
          customer.mobileNumber === '0000000000'
        )
        
        setWalkingCustomer(walking)
        setCustomers(customersData)
        setAllCustomers(customersData)
        console.log('Loaded customers:', customersData.length)
      }
    } catch (error) {
      console.error('Error fetching customers:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = async (query) => {
    setSearchQuery(query)
    console.log('🔍 Searching for:', query)
    
    if (!query.trim()) {
      setCustomers(allCustomers)
      return
    }

    setLoading(true)
    try {
      // Convert mobile number for searching: if starts with 0, replace with 94
      let searchQuery = query
      if (/^\d+$/.test(query) && query.startsWith('0')) {
        searchQuery = '94' + query.substring(1)
        console.log('🔄 Converting mobile for search: 0... → 94... (' + query + ' → ' + searchQuery + ')')
      }

      const localFiltered = allCustomers.filter(customer => {
        const searchLower = query.toLowerCase()
        return (
          customer.name.toLowerCase().includes(searchLower) ||
          customer.mobileNumber.includes(searchQuery)
        )
      })

      if (localFiltered.length > 0) {
        console.log('📱 Local search found:', localFiltered.length, 'customers')
        setCustomers(localFiltered)
        setLoading(false)
        return
      }

      if (/^\d+$/.test(query)) {
        console.log('📱 API Mobile number search for:', searchQuery)
        try {
          const response = await CustomerService.getCustomerByMobile(searchQuery)
          console.log('📱 Mobile search response:', response)
          if (response?.success && response.data) {
            setCustomers([response.data])
          } else {
            const searchResponse = await CustomerService.searchCustomers({
              search: searchQuery,
              limit: 50
            })
            console.log('📱 Search endpoint response:', searchResponse)
            if (searchResponse?.success) {
              setCustomers(searchResponse.data?.customers || [])
            } else {
              setCustomers([])
            }
          }
        } catch (mobileError) {
          console.log('📱 API search failed:', mobileError.message)
          setCustomers([])
        }
      } else {
        console.log('👤 API Name search for:', query)
        try {
          const response = await CustomerService.searchCustomers({
            search: query,
            limit: 50
          })
          console.log('👤 Name search response:', response)
          if (response?.success) {
            setCustomers(response.data?.customers || [])
          } else {
            setCustomers([])
          }
        } catch (nameError) {
          console.log('👤 API name search failed:', nameError.message)
          setCustomers([])
        }
      }
    } catch (error) {
      console.error('Error searching customers:', error)
      setCustomers([])
    } finally {
      setLoading(false)
    }
  }

  const handleCustomerCreated = (newCustomer) => {
    console.log('✅ [CustomerSelectionModalFixed] New customer created:', newCustomer)
    
    // Handle different response structures from API
    const customerData = newCustomer?.customer || newCustomer
    
    console.log('📦 [CustomerSelectionModalFixed] Extracted customer data:', customerData)
    console.log('🔑 [CustomerSelectionModalFixed] Customer ID:', customerData?._id)
    console.log('👤 [CustomerSelectionModalFixed] Customer name:', customerData?.name)
    console.log('📱 [CustomerSelectionModalFixed] Customer mobile:', customerData?.mobileNumber)
    
    setCustomers(prev => [customerData, ...prev])
    setAllCustomers(prev => [customerData, ...prev])
    
    // Automatically select the newly created customer FIRST
    console.log('🎯 [CustomerSelectionModalFixed] Auto-selecting newly created customer:', customerData)
    onCustomerSelect(customerData)
    
    // Don't switch back to customers tab - let the parent close the modal
    // The modal will close in POSBottomNavigation after onCustomerSelect
  }

  const formatDate = (dateString) => {
    if (!dateString) return ''
    try {
      return new Date(dateString).toLocaleDateString('en-GB')
    } catch {
      return ''
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 customer-modal-overlay"
      style={{
        position: 'fixed !important',
        top: '0 !important',
        left: '0 !important',
        right: '0 !important',
        bottom: '0 !important',
        width: '100vw !important',
        height: '100vh !important',
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
        zIndex: '99999999 !important',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose()
        }
      }}
    >
      <div 
        className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[85vh] overflow-hidden relative customer-modal-content"
        style={{ 
          zIndex: '999999999 !important',
          maxWidth: '1200px',
          width: '100%',
          position: 'relative !important'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-orange-200 bg-orange-50">
          <h2 className="text-xl font-semibold text-orange-800">
            {activeTab === 'customers' ? 'Select Customer' : 'Create New Customer'}
          </h2>
          <button
            onClick={onClose}
            className="text-orange-400 hover:text-orange-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-orange-200">
          <button
            onClick={() => setActiveTab('customers')}
            className={`flex-1 px-6 py-4 font-medium transition-colors ${
              activeTab === 'customers'
                ? 'bg-orange-500 text-white'
                : 'bg-white text-orange-600 hover:text-orange-800 hover:bg-orange-50'
            }`}
          >
            All Customers
          </button>
          <button
            onClick={() => setActiveTab('create')}
            className={`flex-1 px-6 py-4 font-medium transition-colors ${
              activeTab === 'create'
                ? 'bg-orange-500 text-white'
                : 'bg-white text-orange-600 hover:text-orange-800 hover:bg-orange-50'
            }`}
          >
            Create Customer
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto" style={{ maxHeight: '65vh' }}>
          {activeTab === 'customers' ? (
            <div className="flex h-full">
              {/* Left Column - Customers */}
              <div className="flex-1 p-6 border-r border-orange-200">
                {/* Search */}
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-orange-400 h-4 w-4" />
                    <Input
                      placeholder="Search by mobile number or name..."
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="pl-10 h-10 text-base border-orange-300 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>

                {/* Walking Customer */}
                {walkingCustomer && (
                  <div className="mb-4">
                    <h3 className="text-xs font-medium text-orange-700 mb-2">Default Customer</h3>
                    <div
                      onClick={() => {
                        console.log('🏃 Walking customer selected:', walkingCustomer);
                        onCustomerSelect(walkingCustomer);
                      }}
                      className={`p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                        walkingCustomer.customerType === 'vip'
                          ? 'bg-gradient-to-br from-yellow-50 to-amber-50 border-amber-300 hover:border-amber-400 shadow-md'
                          : currentCustomerId === walkingCustomer._id
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-orange-200 hover:border-orange-300 hover:bg-orange-50'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <div className={`p-1.5 rounded-full ${
                          walkingCustomer.customerType === 'vip'
                            ? 'bg-amber-100'
                            : 'bg-orange-100'
                        }`}>
                          <User className={`h-4 w-4 ${
                            walkingCustomer.customerType === 'vip'
                              ? 'text-amber-600'
                              : 'text-orange-600'
                          }`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <span className={`font-medium text-sm ${
                              walkingCustomer.customerType === 'vip'
                                ? 'text-amber-900'
                                : 'text-orange-900'
                            }`}>{walkingCustomer.name}</span>
                            <span className={`text-sm ${
                              walkingCustomer.customerType === 'vip'
                                ? 'text-amber-600'
                                : 'text-orange-600'
                            }`}>{CustomerModel.convertMobileForFormInput(walkingCustomer.mobileNumber)}</span>
                            {walkingCustomer.customerType === 'vip' && (
                              <span className="text-xs bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-700 px-2 py-1 rounded-full flex items-center space-x-1 font-semibold shadow-sm">
                                <Star className="h-3 w-3 fill-current" />
                                <span>VIP</span>
                              </span>
                            )}
                            <span className="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded-full">
                              Walking Customer
                            </span>
                            <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full">
                              {walkingCustomer.pointsBalance?.toFixed(2) || '0.00'} points
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Customer List */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs font-medium text-orange-700">
                      {searchQuery ? `Search Results for "${searchQuery}"` : 'All Customers'}
                    </h3>
                    {loading && (
                      <div className="text-xs text-orange-500">Searching...</div>
                    )}
                    {!loading && (
                      <div className="text-xs text-orange-500">
                        {customers.filter(c => c.mobileNumber !== '0000000000').length} customers
                      </div>
                    )}
                  </div>

                  <div className="space-y-1.5 max-h-80 overflow-y-auto">
                    {customers.filter(c => c.mobileNumber !== '0000000000').map((customer) => (
                      <div
                        key={customer._id}
                        onClick={() => {
                          console.log('👤 Regular customer selected:', customer);
                          onCustomerSelect(customer);
                        }}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          customer.customerType === 'vip'
                            ? 'bg-gradient-to-br from-yellow-50 to-amber-50 border-amber-300 hover:border-amber-400 shadow-sm'
                            : currentCustomerId === customer._id
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-orange-200 hover:border-orange-300 hover:bg-orange-50'
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <div className={`p-1.5 rounded-full ${
                            customer.customerType === 'vip'
                              ? 'bg-amber-100'
                              : 'bg-orange-100'
                          }`}>
                            <User className={`h-4 w-4 ${
                              customer.customerType === 'vip'
                                ? 'text-amber-600'
                                : 'text-orange-600'
                            }`} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <span className={`font-medium text-sm ${
                                customer.customerType === 'vip'
                                  ? 'text-amber-900'
                                  : 'text-orange-900'
                              }`}>{customer.name}</span>
                              <span className={`text-sm ${
                                customer.customerType === 'vip'
                                  ? 'text-amber-600'
                                  : 'text-orange-600'
                              }`}>{CustomerModel.convertMobileForFormInput(customer.mobileNumber)}</span>
                              {customer.customerType === 'vip' && (
                                <span className="text-xs bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-700 px-2 py-1 rounded-full flex items-center space-x-1 font-semibold shadow-sm">
                                  <Star className="h-3 w-3 fill-current" />
                                  <span>VIP</span>
                                </span>
                              )}
                              <span className="text-xs text-orange-500">
                                {formatDate(customer.dateOfBirth)}
                              </span>
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                customer.pointsBalance > 0 
                                  ? 'bg-green-100 text-green-600' 
                                  : 'bg-blue-100 text-blue-600'
                              }`}>
                                {customer.pointsBalance?.toFixed(2) || '0.00'} points
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {!loading && customers.filter(c => c.mobileNumber !== '0000000000').length === 0 && (
                      <div className="text-center py-8 text-orange-500">
                        {searchQuery ? (
                          <div>
                            <p className="text-sm">No customers found for "{searchQuery}"</p>
                            <p className="text-xs mt-2 text-orange-400">Try entering a different mobile number or name</p>
                            <button 
                              onClick={() => handleSearch('')}
                              className="text-orange-500 text-xs mt-2 hover:underline"
                            >
                              Show all customers
                            </button>
                          </div>
                        ) : (
                          'No customers available'
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column - Number Pad */}
              <div className="w-96 p-8 bg-orange-50">
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-xs font-medium text-orange-700">Quick mobile number entry:</p>
                    {searchQuery && (
                      <p className="text-xs text-orange-600 font-medium">
                        Searching: {searchQuery}
                      </p>
                    )}
                  </div>
                  <NumericKeyboard
                    onKeyPress={(key) => {
                      const newQuery = searchQuery + key
                      handleSearch(newQuery)
                    }}
                    onDelete={() => {
                      const newQuery = searchQuery.slice(0, -1)
                      handleSearch(newQuery)
                    }}
                    onClear={() => {
                      handleSearch('')
                    }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="flex h-full">
              {/* Left Column - Form */}
              <div className="flex-1 p-6 border-r border-orange-200">
                <CreateCustomerForm
                  onCustomerCreated={handleCustomerCreated}
                  onCancel={() => setActiveTab('customers')}
                  branchId={branchId}
                />
              </div>

              {/* Right Column - Number Pad */}
              <div className="w-96 p-8 bg-orange-50">
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-xs font-medium text-orange-700">Mobile number entry:</p>
                  </div>
                  <NumericKeyboard
                    onKeyPress={(key) => {
                      // This will be handled by the CreateCustomerForm component
                      const event = new CustomEvent('numberPadKeyPress', { detail: key });
                      window.dispatchEvent(event);
                    }}
                    onDelete={() => {
                      const event = new CustomEvent('numberPadDelete');
                      window.dispatchEvent(event);
                    }}
                    onClear={() => {
                      const event = new CustomEvent('numberPadClear');
                      window.dispatchEvent(event);
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CustomerSelectionModal
